// Row model for the "cost per partner" table. Pure (no preact import) so the
// ordering can be unit-tested like the other lib helpers.
//
// The table answers "who costs the most, and on which mode": one total row
// per partner, followed by that partner's modes. The modes run on different
// models at different rates, so the mode row is the grain an invoice line is
// written at — a partner total alone cannot be priced or explained.

import type { ModeTokenStats } from "@/api/types";

export interface TenantCostRow {
  kind: "tenant" | "mode";
  tenant: string;
  /** Set on mode rows only. */
  mode?: string;
  stats: ModeTokenStats;
}

const bySpendDesc = <T>(pick: (item: T) => number) => (a: T, b: T) => pick(b) - pick(a);

/** Flatten the two backend maps into display order: partners by spend, each
 * followed by its modes by spend. A partner absent from `perTenantMode`
 * (older backend) keeps its total row and gets no mode rows. */
export function tenantCostRows(
  perTenant: Record<string, ModeTokenStats>,
  perTenantMode: Record<string, Record<string, ModeTokenStats>> = {},
): TenantCostRow[] {
  const rows: TenantCostRow[] = [];
  const tenants = Object.entries(perTenant).sort(bySpendDesc(([, s]) => s.total_usd));
  for (const [tenant, stats] of tenants) {
    rows.push({ kind: "tenant", tenant, stats });
    const modes = Object.entries(perTenantMode[tenant] ?? {}).sort(
      bySpendDesc(([, s]) => s.total_usd),
    );
    for (const [mode, modeStats] of modes) {
      rows.push({ kind: "mode", tenant, mode, stats: modeStats });
    }
  }
  return rows;
}

/** USD per turn for a row; 0 when the row has no turns. */
export const usdPerTurn = (s: ModeTokenStats): number =>
  s.turns > 0 ? s.total_usd / s.turns : 0;

/** USD per session for a row; 0 when the row has no sessions. */
export const usdPerSession = (s: ModeTokenStats): number =>
  s.sessions > 0 ? s.total_usd / s.sessions : 0;
